import { useState } from 'react'
import { X, Printer, Copy, Check, Download, QrCode, ExternalLink } from 'lucide-react'

interface QrLabelModalProps {
  isOpen: boolean
  onClose: () => void
  code: string
  positionLabel?: string | null
  panelName?: string | null
  projectName?: string | null
  specName?: string | null
  serialNumber?: string | null
}

export type LabelSizePreset = '15x15' | '25x15' | '30x20' | '40x25'

export default function QrLabelModal({
  isOpen,
  onClose,
  code,
  positionLabel,
  panelName,
  projectName,
  specName,
  serialNumber,
}: QrLabelModalProps) {
  const [sizePreset, setSizePreset] = useState<LabelSizePreset>('25x15')
  const [copied, setCopied] = useState(false)

  if (!isOpen) return null

  const shortUrl = `https://e2.bagsus.ru/q/${code.toUpperCase()}`
  const svgUrl = `/api/qr/${encodeURIComponent(code)}/svg?margin=1&level=M`

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shortUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handlePrint = () => {
    const printContent = document.getElementById('printable-qr-label')
    if (!printContent) return

    const printWindow = window.open('', '_blank', 'width=450,height=400')
    if (!printWindow) {
      window.print()
      return
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>QR Label - ${code}</title>
          <style>
            @page {
              size: auto;
              margin: 0;
            }
            body {
              margin: 0;
              padding: 4px;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
              display: flex;
              align-items: center;
              justify-content: center;
              background: #fff;
              color: #000;
            }
            .label-box {
              box-sizing: border-box;
              border: 1px dashed #ccc;
              padding: 2px 4px;
              display: flex;
              align-items: center;
              justify-content: center;
              page-break-inside: avoid;
            }
            img {
              display: block;
              image-rendering: pixelated;
            }
          </style>
        </head>
        <body>
          <div class="label-box">
            ${printContent.innerHTML}
          </div>
          <script>
            window.onload = function() {
              window.focus();
              window.print();
              setTimeout(function() { window.close(); }, 500);
            };
          </script>
        </body>
      </html>
    `)
    printWindow.document.close()
  }

  const handleDownloadSvg = async () => {
    try {
      const resp = await fetch(svgUrl)
      const svgText = await resp.text()
      const blob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `qr-${code.toUpperCase()}.svg`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('Failed to download SVG:', e)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Стикер с QR-кодом</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Format / size preset picker */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Размер стикера (термопринтер / наклейка)
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[
                { id: '15x15', label: '15×15 мм', desc: 'Микро DIN' },
                { id: '25x15', label: '25×15 мм', desc: 'Стандарт' },
                { id: '30x20', label: '30×20 мм', desc: 'С описанием' },
                { id: '40x25', label: '40×25 мм', desc: 'Крупный' },
              ].map((p) => (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setSizePreset(p.id as LabelSizePreset)}
                  className={`px-3 py-2 text-left rounded-lg border text-sm transition-all ${
                    sizePreset === p.id
                      ? 'border-blue-600 bg-blue-50 text-blue-900 font-medium ring-2 ring-blue-500/20'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <div className="font-semibold leading-tight">{p.label}</div>
                  <div className="text-xs text-gray-500 leading-tight mt-0.5">{p.desc}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Sticker Preview Box */}
          <div className="bg-gray-100 p-6 rounded-xl flex flex-col items-center justify-center min-h-[160px] border border-dashed border-gray-300">
            <div
              id="printable-qr-label"
              className="bg-white p-2.5 rounded shadow-sm border border-gray-300 flex items-center justify-center gap-3"
              style={{
                width:
                  sizePreset === '15x15'
                    ? '140px'
                    : sizePreset === '25x15'
                    ? '220px'
                    : sizePreset === '30x20'
                    ? '260px'
                    : '320px',
                minHeight: sizePreset === '15x15' ? '140px' : '90px',
              }}
            >
              {sizePreset === '15x15' ? (
                <div className="flex flex-col items-center justify-center text-center w-full">
                  <img
                    src={svgUrl}
                    alt={code}
                    className="w-24 h-24 object-contain"
                  />
                  <div className="text-[10px] font-mono font-bold tracking-wider text-gray-900 mt-1">
                    E2 • {code.toUpperCase()}
                  </div>
                  {positionLabel && (
                    <div className="text-[9px] font-bold text-blue-800 leading-none">
                      {positionLabel}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-3 w-full">
                  <img
                    src={svgUrl}
                    alt={code}
                    className="w-16 h-16 shrink-0 object-contain"
                  />
                  <div className="flex-1 min-w-0 flex flex-col justify-center">
                    <div className="text-xs font-bold text-gray-900 truncate">
                      {positionLabel ? `Поз. ${positionLabel}` : 'Устройство'}
                    </div>
                    {panelName && (
                      <div className="text-[11px] font-medium text-blue-700 truncate">
                        {panelName}
                      </div>
                    )}
                    {specName && (
                      <div className="text-[10px] text-gray-600 truncate mt-0.5" title={specName}>
                        {specName}
                      </div>
                    )}
                    {projectName && sizePreset === '40x25' && (
                      <div className="text-[9px] text-gray-400 truncate">
                        {projectName}
                      </div>
                    )}
                    {serialNumber && (
                      <div className="text-[9px] font-mono text-gray-500 truncate">
                        S/N: {serialNumber}
                      </div>
                    )}
                    <div className="text-[10px] font-mono font-semibold text-gray-400 mt-1 tracking-wider">
                      E2 • {code.toUpperCase()}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick info & link */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-3 flex items-center justify-between text-xs">
            <div className="min-w-0 pr-2">
              <span className="text-blue-700 font-medium">Ссылка в коде: </span>
              <a
                href={shortUrl}
                target="_blank"
                rel="noreferrer"
                className="font-mono text-blue-900 hover:underline inline-flex items-center gap-1"
              >
                {shortUrl}
                <ExternalLink className="w-3 h-3 inline" />
              </a>
            </div>
            <button
              onClick={handleCopyLink}
              type="button"
              className="shrink-0 flex items-center gap-1 px-2.5 py-1 bg-white border border-blue-200 rounded text-blue-700 hover:bg-blue-100 transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
              <span>{copied ? 'Скопировано' : 'Копировать'}</span>
            </button>
          </div>
        </div>

        {/* Footer actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
          <button
            type="button"
            onClick={handleDownloadSvg}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-colors"
          >
            <Download className="w-4 h-4 text-gray-500" />
            Скачать SVG
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 font-medium"
            >
              Закрыть
            </button>
            <button
              type="button"
              onClick={handlePrint}
              className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 font-medium shadow-sm transition-colors"
            >
              <Printer className="w-4 h-4" />
              Печать стикера
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
